/**
 * @file BackupDestinationFields.tsx
 * @description Поля назначения копий по типу (phase19.26 шаг 7): адрес,
 * путь/бакет/папка и учётные данные из реестра `DESTINATION_META`.
 *
 * Поля секретов всегда пустые: сохранённые данные сервер не отдаёт. При
 * `hasCredentials` плейсхолдер «••••• сохранён», подпись «не трогайте — останется
 * как есть». На 360 поля идут колонкой, с md — по два в ряд. SMB — пароль
 * необязателен (гостевая общая папка, шаг 7.1).
 */

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { SelectField } from "../../ui/SelectField";
import { Textarea } from "../../ui/textarea";
import { DESTINATION_META, type DestinationDraft } from "./backup-destination";

const SFTP_AUTH_OPTIONS = [
  { value: "password", label: "По паролю" },
  { value: "key", label: "По закрытому ключу" },
];

interface Props {
  draft: DestinationDraft;
  onChange: (patch: Partial<DestinationDraft>) => void;
  /** Данные на сервере — только если тип не меняли (иначе они не подходят). */
  saved: { hasCredentials: boolean; credentialsLogin: string | null } | null;
  disabled?: boolean;
}

export function BackupDestinationFields({ draft, onChange, saved, disabled }: Props) {
  const meta = DESTINATION_META[draft.type];
  const secretPlaceholder = saved?.hasCredentials ? "••••• сохранён" : "";
  const text = (id: string, label: string, key: keyof DestinationDraft, extra: Partial<React.ComponentProps<typeof Input>> = {}) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={String(draft[key])}
        onChange={(e) => onChange({ [key]: e.target.value } as Partial<DestinationDraft>)}
        disabled={disabled}
        {...extra}
      />
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {meta.url ? text("bk-dst-url", meta.url.label, "url", { placeholder: meta.url.placeholder, className: "font-mono" }) : null}
      {text("bk-dst-path", meta.path.label, "path", { placeholder: meta.path.placeholder, className: "font-mono" })}

      {meta.credentials === "s3" ? (
        <>
          {text("bk-dst-ak", "Ключ доступа (Access Key)", "accessKey", { placeholder: secretPlaceholder, autoComplete: "off" })}
          {text("bk-dst-sk", "Секретный ключ (Secret Key)", "secretKey", { type: "password", placeholder: secretPlaceholder, autoComplete: "new-password" })}
          {text("bk-dst-region", "Регион (если требуется)", "region", { placeholder: "ru-central1" })}
        </>
      ) : null}

      {meta.credentials === "userPassword" ||
      meta.credentials === "userOptionalPassword" ||
      meta.credentials === "userPasswordOrKey" ? (
        <>
          {text("bk-dst-user", "Логин", "user", {
            placeholder: saved?.credentialsLogin ? `${saved.credentialsLogin} — сохранён` : "",
            autoComplete: "off",
          })}
          {meta.credentials === "userPasswordOrKey" ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-dst-auth">Способ входа</Label>
              <SelectField
                id="bk-dst-auth"
                ariaLabel="Способ входа"
                value={draft.sftpAuth}
                onChange={(v) => onChange({ sftpAuth: v === "key" ? "key" : "password" })}
                options={SFTP_AUTH_OPTIONS}
                disabled={disabled}
              />
            </div>
          ) : null}
          {meta.credentials === "userPasswordOrKey" && draft.sftpAuth === "key" ? (
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="bk-dst-key">Закрытый ключ</Label>
              <Textarea
                id="bk-dst-key"
                rows={4}
                value={draft.privateKey}
                onChange={(e) => onChange({ privateKey: e.target.value })}
                placeholder={secretPlaceholder || "-----BEGIN OPENSSH PRIVATE KEY-----"}
                disabled={disabled}
                spellCheck={false}
                className="font-mono text-xs"
              />
            </div>
          ) : (
            text("bk-dst-pass", meta.credentials === "userOptionalPassword" ? "Пароль (если папка не гостевая)" : "Пароль", "password", { type: "password", placeholder: secretPlaceholder, autoComplete: "new-password" })
          )}
        </>
      ) : null}

      {saved?.hasCredentials && meta.credentials !== "none" ? (
        <p className="text-xs text-on-surface-variant md:col-span-2">
          Учётные данные сохранены. Оставьте поля пустыми — останутся как есть; заполните — заменятся.
        </p>
      ) : null}
    </div>
  );
}
